import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import type { RootState } from "./index";
import { CustomerModel } from "../models";
import { GetUserApi, Logout } from "../services";

// ---- Types ----
export type TokenModel = {
  access_token: string;
  token_type: string;
};

interface AuthState {
  deviceId: string | null;
  token: TokenModel | null;
  customer: CustomerModel;
  isUserLoading: boolean;
  hasFetchedUser: boolean;
}

// ---- Default customer (same as your context) ----
const defaultCustomer: CustomerModel = {
  id: "",
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  customer_dob: "",
  type: "guest",
};

// ---- LocalStorage initializers ----
const initialDeviceId = (() => {
  try {
    const raw = localStorage.getItem("device_id");
    return raw ? (JSON.parse(raw) as string | null) : null;
  } catch {
    return null;
  }
})();

const initialToken = (() => {
  try {
    const raw = localStorage.getItem("token");
    return raw ? (JSON.parse(raw) as TokenModel | null) : null;
  } catch {
    return null;
  }
})();

const initialCustomer = (() => {
  try {
    const raw = localStorage.getItem("customer");
    return raw ? (JSON.parse(raw) as CustomerModel) : defaultCustomer;
  } catch {
    return defaultCustomer;
  }
})();

const initialState: AuthState = {
  deviceId: initialDeviceId,
  token: initialToken,
  customer: initialCustomer,
  isUserLoading: false,
  hasFetchedUser: false,
};

// Thunk: logout and invalidate token on server
export const logoutUser = createAsyncThunk<void, void, { state: RootState }>(
  "auth/logout",
  async () => {
    try {
      await Logout();
    } catch {
      // proceed with local logout even if server call fails
    }
  },
);

// 🔥 Thunk: fetch current user (with condition)
export const fetchUser = createAsyncThunk<
  CustomerModel,
  void,
  { state: RootState; rejectValue: string }
>(
  "auth/fetchUser",
  async (_, { getState, rejectWithValue }) => {
    const { storeId } = getState().store;

    try {
      const { status, message, data } = await GetUserApi({ store_id: storeId });
      if (!status) {
        console.error(message);
        return rejectWithValue(message);
      }
      return data as CustomerModel;
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      return rejectWithValue("Failed to fetch user data");
    }
  },
  {
    // ⛔ Guard: avoid repeated calls
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { auth, store } = state;

      // if we've already fetched once, skip
      if (auth.hasFetchedUser) return false;

      // must have token
      if (!auth.token) return false;

      // must have store id
      if (!store.storeId) return false;

      // must be logged in and not guest
      const isLoggedIn = Boolean(
        auth.customer && auth.customer.id && auth.token,
      );
      const isGuest = auth.customer?.id === "0";
      if (!isLoggedIn || isGuest) return false;

      // skip if already loading
      if (auth.isUserLoading) return false;

      return true;
    },
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setDeviceId(state, action: PayloadAction<string | null>) {
      state.deviceId = action.payload;
      try {
        if (action.payload === null) localStorage.removeItem("device_id");
        else localStorage.setItem("device_id", JSON.stringify(action.payload));
      } catch {}
    },
    setToken(state, action: PayloadAction<TokenModel | null>) {
      state.token = action.payload;
      try {
        if (action.payload === null) localStorage.removeItem("token");
        else localStorage.setItem("token", JSON.stringify(action.payload));
      } catch {}
      // token changed → reset user fetch state
      state.hasFetchedUser = false;
    },
    setCustomer(state, action: PayloadAction<CustomerModel>) {
      state.customer = action.payload;
      try {
        localStorage.setItem("customer", JSON.stringify(action.payload));
      } catch {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.isUserLoading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isUserLoading = false;
        state.customer = action.payload;
        state.hasFetchedUser = true; // 👈 mark as done
        try {
          localStorage.setItem("customer", JSON.stringify(action.payload));
        } catch {}
      })
      .addCase(fetchUser.rejected, (state) => {
        state.isUserLoading = false;
        state.hasFetchedUser = true; // 👈 also mark as done to avoid retry loop
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.token = null;
        state.deviceId = null;
        state.customer = defaultCustomer;
        state.hasFetchedUser = false;
        try {
          localStorage.removeItem("token");
          localStorage.removeItem("device_id");
          localStorage.setItem("customer", JSON.stringify(defaultCustomer));
        } catch {}
      })
      .addCase(logoutUser.rejected, (state) => {
        state.token = null;
        state.deviceId = null;
        state.customer = defaultCustomer;
        state.hasFetchedUser = false;
        try {
          localStorage.removeItem("token");
          localStorage.removeItem("device_id");
          localStorage.setItem("customer", JSON.stringify(defaultCustomer));
        } catch {}
      });
  },
});

// selectors (unchanged)
export const { setDeviceId, setToken, setCustomer } = authSlice.actions;
export { logoutUser as logout };
export default authSlice.reducer;

export const selectAuthState = (state: RootState) => state.auth;
export const selectCustomer = (state: RootState) => state.auth.customer;

export const selectIsLoggedIn = createSelector(selectAuthState, (auth) =>
  Boolean(auth.customer && auth.customer.id && auth.token),
);

export const selectIsGuest = createSelector(selectCustomer, (customer) =>
  Boolean(customer && customer.id === "0"),
);

export const selectCustomerId = createSelector(
  selectCustomer,
  (customer) => customer?.id || "",
);
